// src/pages/StopGoContinueAnalyst.jsx
//
// Admin view for the Stop / Go / Continue PD activity.
// Tabs: Whole Session / By Topic / By Column.
// Each view's synthesis is cached in /stopGoContinueSyntheses/{viewId}.
// "Regenerate" forces a fresh run via the synthesizeStopGoContinue
// Cloud Function.
//
// Access: gated by ToolRoute toolId="stopGoContinueAnalyst" upstream,
// and re-checked server-side in the Cloud Function.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { downloadSynthesisPdf } from '../utils/sgcAnalystPdf';

// Match the deployed Cloud Function URL pattern used elsewhere in the app
// (e.g. anthropicProxy, generatePDSummary). All your functions live in
// us-central1 on the kings-app-74847 project.
const FUNCTION_URL =
  'https://us-central1-kings-app-74847.cloudfunctions.net/synthesizeStopGoContinue';

// ── Topic / column schema (must match StopGoContinue.jsx) ───────────────
const TOPICS = [
  { id: 'cocosAthletics', label: 'Co-Curriculars & Athletics' },
  { id: 'resLife',        label: 'Student / Faculty Residential Life' },
  { id: 'academics',      label: 'Academics' },
  { id: 'facultyCulture', label: 'Faculty Culture' },
  { id: 'dining',         label: 'Dining' },
  { id: 'mentalHealth',   label: 'Mental Health' },
  { id: 'behavior',       label: 'Student Behavior' },
  { id: 'weekends',       label: 'Weekends' },
  { id: 'arts',           label: 'Arts' },
];

const COLUMNS = [
  { id: 'stop',     label: 'STOP',     color: '#E83530' },
  { id: 'go',       label: 'GO',       color: '#2E7D32' },
  { id: 'continue', label: 'CONTINUE', color: '#233C5F' },
];

const KA = {
  red:   '#E83530',
  navy:  '#233C5F',
  gold:  '#A29164',
  paper: '#FAF8F4',
  ink:   '#1A1A1A',
  mute:  '#6B6B6B',
  line:  '#E5E1D8',
  paleGold: '#FFF8EC',
};

// ── Component ───────────────────────────────────────────────────────────
export default function StopGoContinueAnalyst() {
  const { user, isSuperAdmin } = useAuth();

  // Tab: 'session' | 'topic' | 'column'
  const [tab, setTab] = useState('session');
  const [topicId, setTopicId] = useState(TOPICS[0].id);
  const [columnId, setColumnId] = useState(COLUMNS[0].id);

  // Submissions (raw, for the inventory panel + "no data yet" state).
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoaded, setSubmissionsLoaded] = useState(false);

  // Cached synthesis docs (viewId → doc).
  const [syntheses, setSyntheses] = useState({});

  // Per-view regenerate state: 'idle' | 'running' | 'error'
  const [runState, setRunState] = useState({});
  const [runError, setRunError] = useState('');

  // -----------------------------------------------------
  // Subscribe to submissions (count + scribes table)
  // -----------------------------------------------------
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'stopGoContinue'),
      (snap) => {
        const subs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const ta = a.createdAt?.toMillis?.() || 0;
            const tb = b.createdAt?.toMillis?.() || 0;
            return tb - ta;
          });
        setSubmissions(subs);
        setSubmissionsLoaded(true);
      },
      (err) => {
        console.error('Submissions listener error', err);
        setSubmissionsLoaded(true);
      }
    );
    return () => unsub();
  }, []);

  // -----------------------------------------------------
  // Subscribe to all synthesis docs
  // -----------------------------------------------------
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'stopGoContinueSyntheses'),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => { map[d.id] = d.data(); });
        setSyntheses(map);
      },
      (err) => console.error('Syntheses listener error', err)
    );
    return () => unsub();
  }, []);

  // -----------------------------------------------------
  // Current view id, derived from tab selection
  // -----------------------------------------------------
  const viewId = useMemo(() => {
    if (tab === 'session') return 'whole-session';
    if (tab === 'topic')   return `topic-${topicId}`;
    if (tab === 'column')  return `column-${columnId}`;
    return 'whole-session';
  }, [tab, topicId, columnId]);

  const synthesis = syntheses[viewId];
  const isStale = synthesis && synthesis.inputCount !== submissions.length;

  // -----------------------------------------------------
  // Regenerate
  // -----------------------------------------------------
  const regenerate = useCallback(async () => {
    setRunError('');
    setRunState((s) => ({ ...s, [viewId]: 'running' }));
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Not signed in.');
      const resp = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ viewId }),
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
          const body = await resp.json();
          if (body?.error) msg = body.error;
        } catch (e) { /* ignore */ }
        throw new Error(msg);
      }
      setRunState((s) => ({ ...s, [viewId]: 'idle' }));
    } catch (e) {
      console.error('Synthesis failed', e);
      setRunError(e.message || 'Synthesis failed.');
      setRunState((s) => ({ ...s, [viewId]: 'error' }));
    }
  }, [viewId]);

  const isRunning = runState[viewId] === 'running';

  // -----------------------------------------------------
  // Download PDF (lazy-loads pdfmake on first click)
  // -----------------------------------------------------
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const downloadPdf = useCallback(async () => {
    if (!synthesis) return;
    setDownloadError('');
    setDownloading(true);
    try {
      const generatedAt = synthesis.generatedAt?.toDate
        ? synthesis.generatedAt.toDate()
        : new Date(synthesis.generatedAt || Date.now());
      await downloadSynthesisPdf({
        synthesis: synthesis.synthesis,
        meta: {
          title: viewLabel(viewId),
          inputCount: synthesis.inputCount || 0,
          generatedAt,
        },
      });
    } catch (e) {
      console.error('PDF download failed', e);
      setDownloadError(e.message || 'Could not generate PDF.');
    } finally {
      setDownloading(false);
    }
  }, [synthesis, viewId]);

  // -----------------------------------------------------
  // Render
  // -----------------------------------------------------
  if (!user) {
    return (
      <div style={styles.shell}>
        <div style={styles.card}>
          <h2 style={styles.h2}>Please sign in</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.shell}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Stop / Go / Continue · Analyst</h1>
          <div style={styles.subhead}>
            Synthesized views of faculty PD submissions · King's Academy, Madaba, Jordan
          </div>
        </div>
        <SubmissionsBadge
          count={submissions.length}
          loaded={submissionsLoaded}
        />
      </div>

      {/* Tab bar */}
      <div style={styles.tabBar}>
        <TabButton active={tab === 'session'} onClick={() => setTab('session')}>
          Whole Session
        </TabButton>
        <TabButton active={tab === 'topic'} onClick={() => setTab('topic')}>
          By Topic
        </TabButton>
        <TabButton active={tab === 'column'} onClick={() => setTab('column')}>
          By Column
        </TabButton>
      </div>

      {/* Sub-selectors */}
      {tab === 'topic' && (
        <div style={styles.subSelector}>
          {TOPICS.map((t) => (
            <Chip
              key={t.id}
              active={t.id === topicId}
              onClick={() => setTopicId(t.id)}
            >
              {t.label}
            </Chip>
          ))}
        </div>
      )}
      {tab === 'column' && (
        <div style={styles.subSelector}>
          {COLUMNS.map((c) => (
            <Chip
              key={c.id}
              active={c.id === columnId}
              onClick={() => setColumnId(c.id)}
              color={c.color}
            >
              {c.label}
            </Chip>
          ))}
        </div>
      )}

      {/* Synthesis card */}
      <div style={styles.card}>
        <SynthesisHeader
          viewId={viewId}
          synthesis={synthesis}
          isStale={isStale}
          isRunning={isRunning}
          submissionsCount={submissions.length}
          onRegenerate={regenerate}
          onDownloadPdf={downloadPdf}
          downloading={downloading}
        />

        {runError && (
          <div style={styles.error}>{runError}</div>
        )}

        {downloadError && (
          <div style={styles.error}>{downloadError}</div>
        )}

        {!synthesis && !isRunning && (
          <EmptyState
            submissionsCount={submissions.length}
            onGenerate={regenerate}
          />
        )}

        {isRunning && <LoadingState />}

        {synthesis && (
          <SynthesisBody
            synthesis={synthesis.synthesis}
            scope={synthesis.scope}
          />
        )}
      </div>

      {/* Submissions inventory */}
      {(isSuperAdmin || submissions.length > 0) && (
        <details style={styles.inventory}>
          <summary style={styles.inventoryToggle}>
            Submissions inventory ({submissions.length})
          </summary>
          <SubmissionsTable submissions={submissions} />
        </details>
      )}
    </div>
  );
}

// =====================================================
// Sub-components
// =====================================================

function SubmissionsBadge({ count, loaded }) {
  if (!loaded) return null;
  return (
    <div style={styles.badge}>
      <div style={styles.badgeNum}>{count}</div>
      <div style={styles.badgeLabel}>
        {count === 1 ? 'submission' : 'submissions'}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.tabBtn,
        background: active ? 'white' : 'transparent',
        color: active ? KA.navy : KA.mute,
        borderBottom: active ? `2px solid ${KA.navy}` : '2px solid transparent',
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function Chip({ active, onClick, children, color }) {
  const bg = active ? (color || KA.navy) : 'white';
  const fg = active ? 'white' : KA.ink;
  const bd = active ? (color || KA.navy) : KA.line;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.chip,
        background: bg,
        color: fg,
        borderColor: bd,
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function SynthesisHeader({
  viewId,
  synthesis,
  isStale,
  isRunning,
  submissionsCount,
  onRegenerate,
  onDownloadPdf,
  downloading,
}) {
  const label = viewLabel(viewId);
  const canDownload = !!synthesis && !isRunning && !downloading;
  return (
    <div style={styles.synthHeader}>
      <div>
        <div style={styles.synthEyebrow}>Synthesis</div>
        <h2 style={styles.h2}>{label}</h2>
        {synthesis && (
          <div style={styles.synthMeta}>
            Generated {formatTimestamp(synthesis.generatedAt)} · based on{' '}
            <b>{synthesis.inputCount}</b>{' '}
            {synthesis.inputCount === 1 ? 'submission' : 'submissions'}
            {isStale && (
              <span style={styles.staleTag}>
                {submissionsCount - synthesis.inputCount > 0 ? '+' : ''}
                {submissionsCount - synthesis.inputCount} new since
              </span>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {synthesis && (
          <button
            type="button"
            onClick={onDownloadPdf}
            disabled={!canDownload}
            style={{
              ...styles.downloadBtn,
              opacity: canDownload ? 1 : 0.4,
              cursor: canDownload ? 'pointer' : 'not-allowed',
            }}
            title="Download this synthesis as a PDF"
          >
            {downloading ? 'Building PDF…' : '⬇ Download PDF'}
          </button>
        )}
        <button
          type="button"
          onClick={onRegenerate}
          disabled={isRunning || submissionsCount === 0}
          style={{
            ...styles.regenBtn,
            opacity: isRunning || submissionsCount === 0 ? 0.4 : 1,
            cursor: isRunning || submissionsCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {isRunning ? 'Generating…' : synthesis ? 'Regenerate' : 'Generate'}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ submissionsCount, onGenerate }) {
  if (submissionsCount === 0) {
    return (
      <div style={styles.empty}>
        <div style={styles.emptyTitle}>No submissions yet</div>
        <div style={styles.emptyBody}>
          Once faculty groups submit their Stop / Go / Continue responses,
          you can run a synthesis here.
        </div>
      </div>
    );
  }
  return (
    <div style={styles.empty}>
      <div style={styles.emptyTitle}>Not generated yet</div>
      <div style={styles.emptyBody}>
        Click <b>Generate</b> above to synthesize this view across{' '}
        {submissionsCount}{' '}
        {submissionsCount === 1 ? 'submission' : 'submissions'}.
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={styles.empty}>
      <div style={styles.emptyTitle}>Working on it…</div>
      <div style={styles.emptyBody}>
        Reading submissions, scrubbing names, and calling Claude. This usually
        takes 20–60 seconds depending on the slice.
      </div>
    </div>
  );
}

// -----------------------------------------------------
// Synthesis body — renders the structured JSON output
// -----------------------------------------------------
function SynthesisBody({ synthesis, scope }) {
  if (!synthesis || typeof synthesis !== 'object') {
    return <div style={styles.error}>Synthesis result was empty or malformed.</div>;
  }

  return (
    <div>
      {synthesis.headline && (
        <div style={styles.headline}>
          <div style={styles.headlineLabel}>HEADLINE</div>
          <div style={styles.headlineText}>{synthesis.headline}</div>
        </div>
      )}

      <Section title="Themes & frequency" subtitle="Patterns that came up across multiple groups.">
        {(synthesis.themes || []).map((t, i) => (
          <ThemeCard key={i} theme={t} scope={scope} />
        ))}
        {(synthesis.themes || []).length === 0 && (
          <div style={styles.sectionEmpty}>No themes surfaced for this slice.</div>
        )}
      </Section>

      <Section title="Action orientation" subtitle="Concrete actions vs. abstract values.">
        {synthesis.actionOrientation?.note && (
          <div style={styles.aoNote}>{synthesis.actionOrientation.note}</div>
        )}
        <div style={styles.aoGrid}>
          <AOCol
            heading="Concrete"
            blurb="Specific, doable changes"
            items={synthesis.actionOrientation?.concrete || []}
          />
          <AOCol
            heading="Abstract"
            blurb="Values, principles, aspirations"
            items={synthesis.actionOrientation?.abstract || []}
          />
        </div>
      </Section>

      <Section title="Where groups disagreed" subtitle="Tensions to be aware of.">
        {(synthesis.tensions || []).length === 0 ? (
          <div style={styles.sectionEmpty}>No clear tensions surfaced in this slice.</div>
        ) : (
          (synthesis.tensions || []).map((t, i) => (
            <TensionCard key={i} tension={t} />
          ))
        )}
      </Section>

      <Section title="Quick wins vs. structural changes" subtitle="What's actionable now vs. what requires bigger shifts.">
        <div style={styles.aoGrid}>
          <AOCol
            heading="Quick wins"
            blurb="Doable in weeks, low cost"
            items={synthesis.quickWinsVsStructural?.quickWins || []}
            tint={KA.paleGold}
          />
          <AOCol
            heading="Structural"
            blurb="Months/years, policy/budget"
            items={synthesis.quickWinsVsStructural?.structural || []}
          />
        </div>
      </Section>

      <Section title="Surfaced from brainstorm" subtitle="Items raised in brainstorming but not elevated to any group's top 5 — worth a second look.">
        {(synthesis.surfacedFromBrainstorm || []).length === 0 ? (
          <div style={styles.sectionEmpty}>Nothing notable surfaced beneath the top picks.</div>
        ) : (
          (synthesis.surfacedFromBrainstorm || []).map((s, i) => (
            <SurfacedCard key={i} surfaced={s} />
          ))
        )}
      </Section>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionHeader}>
        <h3 style={styles.h3}>{title}</h3>
        {subtitle && <div style={styles.sectionSubtitle}>{subtitle}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ThemeCard({ theme, scope }) {
  return (
    <div style={styles.themeCard}>
      <div style={styles.themeHead}>
        <div style={styles.themeLabel}>{theme.label}</div>
        {typeof theme.groupCount === 'number' && (
          <div style={styles.themeCount}>
            {theme.groupCount} {theme.groupCount === 1 ? 'group' : 'groups'}
          </div>
        )}
      </div>
      <div style={styles.themeSummary}>{theme.summary}</div>
      {scope !== 'column' && theme.columnDistribution && (
        <div style={styles.themeDist}>
          {COLUMNS.map((c) => {
            const n = theme.columnDistribution[c.id] || 0;
            if (!n) return null;
            return (
              <span key={c.id} style={{
                ...styles.themeDistPill,
                background: c.color,
              }}>
                {c.label} · {n}
              </span>
            );
          })}
        </div>
      )}
      {Array.isArray(theme.examples) && theme.examples.length > 0 && (
        <ul style={styles.themeExamples}>
          {theme.examples.map((ex, i) => (
            <li key={i}>{ex}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AOCol({ heading, blurb, items, tint }) {
  return (
    <div style={{ ...styles.aoCol, background: tint || 'white' }}>
      <div style={styles.aoHead}>{heading}</div>
      <div style={styles.aoBlurb}>{blurb}</div>
      {items.length === 0 ? (
        <div style={styles.aoEmpty}>(none in this slice)</div>
      ) : (
        items.map((it, i) => (
          <div key={i} style={styles.aoItem}>
            <div style={styles.aoItemLabel}>{it.label}</div>
            {it.summary && <div style={styles.aoItemSummary}>{it.summary}</div>}
            {Array.isArray(it.examples) && it.examples.length > 0 && (
              <ul style={styles.aoItemExamples}>
                {it.examples.map((ex, j) => <li key={j}>{ex}</li>)}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function TensionCard({ tension }) {
  return (
    <div style={styles.tensionCard}>
      <div style={styles.tensionLabel}>{tension.label}</div>
      <div style={styles.tensionGrid}>
        <div style={styles.tensionSide}>
          <div style={styles.tensionSideLabel}>Side A</div>
          <div style={styles.tensionSideSummary}>{tension.sideA?.summary}</div>
          {Array.isArray(tension.sideA?.examples) && (
            <ul style={styles.themeExamples}>
              {tension.sideA.examples.map((ex, i) => <li key={i}>{ex}</li>)}
            </ul>
          )}
        </div>
        <div style={styles.tensionSide}>
          <div style={styles.tensionSideLabel}>Side B</div>
          <div style={styles.tensionSideSummary}>{tension.sideB?.summary}</div>
          {Array.isArray(tension.sideB?.examples) && (
            <ul style={styles.themeExamples}>
              {tension.sideB.examples.map((ex, i) => <li key={i}>{ex}</li>)}
            </ul>
          )}
        </div>
      </div>
      {tension.note && (
        <div style={styles.tensionNote}>{tension.note}</div>
      )}
    </div>
  );
}

function SurfacedCard({ surfaced }) {
  return (
    <div style={styles.surfacedCard}>
      <div style={styles.surfacedLabel}>{surfaced.label}</div>
      {surfaced.summary && (
        <div style={styles.surfacedSummary}>{surfaced.summary}</div>
      )}
      {Array.isArray(surfaced.examples) && surfaced.examples.length > 0 && (
        <ul style={styles.themeExamples}>
          {surfaced.examples.map((ex, i) => <li key={i}>{ex}</li>)}
        </ul>
      )}
    </div>
  );
}

function SubmissionsTable({ submissions }) {
  if (submissions.length === 0) {
    return <div style={{ padding: 16, color: KA.mute }}>No submissions yet.</div>;
  }
  return (
    <div style={{ padding: '0 16px 16px' }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Table</th>
            <th style={styles.th}>Scribe</th>
            <th style={styles.th}>Submitted</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((s) => (
            <tr key={s.id}>
              <td style={styles.td}>{s.tableNumber || '—'}</td>
              <td style={styles.td}>{s.scribeName || s.scribeEmail || '—'}</td>
              <td style={styles.td}>{formatTimestamp(s.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =====================================================
// Helpers
// =====================================================
function viewLabel(viewId) {
  if (viewId === 'whole-session') return 'Whole Session';
  if (viewId.startsWith('topic-')) {
    const tid = viewId.slice('topic-'.length);
    return TOPICS.find((t) => t.id === tid)?.label || tid;
  }
  if (viewId.startsWith('column-')) {
    const cid = viewId.slice('column-'.length);
    return `${COLUMNS.find((c) => c.id === cid)?.label || cid} · all topics`;
  }
  return viewId;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// =====================================================
// Styles
// =====================================================
const styles = {
  shell: {
    minHeight: '100vh',
    background: KA.paper,
    padding: '24px 16px 80px',
    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
    color: KA.ink,
  },
  header: {
    maxWidth: 1100,
    margin: '0 auto 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 16,
  },
  h1: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 4px',
    fontSize: 28,
  },
  h2: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 4px',
    fontSize: 22,
  },
  h3: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 4px',
    fontSize: 18,
  },
  subhead: {
    color: KA.mute,
    fontSize: 13,
  },
  badge: {
    background: 'white',
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    padding: '6px 14px',
    textAlign: 'center',
    minWidth: 80,
  },
  badgeNum: {
    fontFamily: 'Georgia, serif',
    fontSize: 24,
    color: KA.navy,
    fontWeight: 700,
    lineHeight: 1,
  },
  badgeLabel: {
    fontSize: 11,
    color: KA.mute,
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  tabBar: {
    maxWidth: 1100,
    margin: '0 auto 12px',
    display: 'flex',
    gap: 4,
    borderBottom: `1px solid ${KA.line}`,
  },
  tabBtn: {
    background: 'transparent',
    border: 'none',
    padding: '10px 18px',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s',
  },
  subSelector: {
    maxWidth: 1100,
    margin: '0 auto 16px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    padding: '6px 14px',
    border: '1px solid',
    borderRadius: 999,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  card: {
    maxWidth: 1100,
    margin: '0 auto',
    background: 'white',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
    border: `1px solid ${KA.line}`,
  },
  synthHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 16,
    borderBottom: `1px solid ${KA.line}`,
    marginBottom: 20,
    gap: 16,
  },
  synthEyebrow: {
    color: KA.gold,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    marginBottom: 4,
  },
  synthMeta: {
    color: KA.mute,
    fontSize: 12,
    marginTop: 4,
  },
  staleTag: {
    marginLeft: 8,
    padding: '2px 8px',
    background: '#FFE4D9',
    color: '#A0410D',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  regenBtn: {
    background: KA.red,
    color: 'white',
    border: 'none',
    padding: '9px 18px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
  },
  downloadBtn: {
    background: 'white',
    color: KA.navy,
    border: `1px solid ${KA.navy}`,
    padding: '8px 14px',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
  },
  empty: {
    padding: '40px 20px',
    textAlign: 'center',
    color: KA.mute,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: KA.ink,
    marginBottom: 6,
    fontFamily: 'Georgia, serif',
  },
  emptyBody: {
    fontSize: 13,
    maxWidth: 480,
    margin: '0 auto',
  },
  error: {
    background: '#FFEBEE',
    color: KA.red,
    border: `1px solid ${KA.red}`,
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 16,
  },
  headline: {
    background: KA.paleGold,
    border: `1px solid ${KA.gold}`,
    borderRadius: 8,
    padding: '14px 18px',
    marginBottom: 24,
  },
  headlineLabel: {
    fontSize: 10,
    color: KA.gold,
    fontWeight: 800,
    letterSpacing: 1.5,
    marginBottom: 4,
  },
  headlineText: {
    fontSize: 16,
    color: KA.ink,
    fontFamily: 'Georgia, serif',
    lineHeight: 1.45,
  },
  section: {
    marginBottom: 28,
    paddingBottom: 8,
  },
  sectionHeader: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: `1px solid ${KA.line}`,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: KA.mute,
    marginTop: 2,
  },
  sectionEmpty: {
    color: KA.mute,
    fontSize: 13,
    fontStyle: 'italic',
    padding: '8px 0',
  },
  themeCard: {
    background: '#FDFCFA',
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    padding: 14,
    marginBottom: 10,
  },
  themeHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 4,
  },
  themeLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: KA.navy,
    fontFamily: 'Georgia, serif',
  },
  themeCount: {
    background: KA.navy,
    color: 'white',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  themeSummary: {
    color: KA.ink,
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 8,
  },
  themeDist: {
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  themeDistPill: {
    color: 'white',
    padding: '1px 8px',
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  themeExamples: {
    margin: '4px 0 0',
    paddingLeft: 20,
    fontSize: 12,
    color: KA.ink,
    lineHeight: 1.45,
  },
  aoNote: {
    color: KA.ink,
    fontSize: 13,
    lineHeight: 1.5,
    marginBottom: 12,
    padding: '8px 12px',
    background: '#FDFCFA',
    border: `1px solid ${KA.line}`,
    borderRadius: 6,
  },
  aoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  aoCol: {
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    padding: 14,
  },
  aoHead: {
    fontFamily: 'Georgia, serif',
    fontSize: 15,
    color: KA.navy,
    fontWeight: 700,
    marginBottom: 2,
  },
  aoBlurb: {
    fontSize: 11,
    color: KA.mute,
    marginBottom: 10,
    fontStyle: 'italic',
  },
  aoEmpty: {
    fontSize: 12,
    color: KA.mute,
    fontStyle: 'italic',
  },
  aoItem: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottom: `1px solid ${KA.line}`,
  },
  aoItemLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: KA.ink,
  },
  aoItemSummary: {
    fontSize: 12,
    color: KA.mute,
    marginTop: 2,
    lineHeight: 1.45,
  },
  aoItemExamples: {
    margin: '4px 0 0',
    paddingLeft: 18,
    fontSize: 11,
    color: KA.ink,
  },
  tensionCard: {
    background: '#FDFCFA',
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
  },
  tensionLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: KA.navy,
    fontFamily: 'Georgia, serif',
    marginBottom: 8,
  },
  tensionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  tensionSide: {
    background: 'white',
    border: `1px solid ${KA.line}`,
    borderRadius: 6,
    padding: 10,
  },
  tensionSideLabel: {
    fontSize: 10,
    color: KA.gold,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  tensionSideSummary: {
    fontSize: 12,
    color: KA.ink,
    lineHeight: 1.45,
  },
  tensionNote: {
    marginTop: 10,
    padding: '8px 12px',
    background: KA.paleGold,
    border: `1px solid ${KA.gold}`,
    borderRadius: 6,
    fontSize: 12,
    color: KA.ink,
    lineHeight: 1.5,
  },
  surfacedCard: {
    background: '#FDFCFA',
    border: `1px solid ${KA.line}`,
    borderLeft: `3px solid ${KA.gold}`,
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  surfacedLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: KA.navy,
    fontFamily: 'Georgia, serif',
  },
  surfacedSummary: {
    fontSize: 12,
    color: KA.ink,
    lineHeight: 1.45,
    marginTop: 2,
  },
  inventory: {
    maxWidth: 1100,
    margin: '20px auto 0',
    background: 'white',
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
  },
  inventoryToggle: {
    padding: '12px 16px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: KA.navy,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    borderBottom: `1px solid ${KA.line}`,
    color: KA.mute,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  td: {
    padding: '6px 8px',
    borderBottom: `1px solid ${KA.line}`,
  },
};